import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { searchDevices } from '../../services/devices';
import { addUserDevice } from '../../services/userDevices';
import type { Device } from '../../types/models';

interface TrackDeviceModalProps {
  open: boolean;
  onClose: () => void;
}

export function TrackDeviceModal({ open, onClose }: TrackDeviceModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (deviceId: string) =>
      addUserDevice({ deviceId, batteryHealthPct: 100, purchasedAt: new Date().toISOString() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'devices'] });
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      searchDevices(trimmed)
        .then((devices) => {
          if (requestId.current === id) setResults(devices.slice(0, 8));
        })
        .catch(() => {
          if (requestId.current === id) setResults([]);
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Add a device to track"
    >
      <div className="panel w-full max-w-md p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">Add a device to track</h3>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the catalog…"
          className="input-panel"
        />

        <div className="flex flex-col gap-1 max-h-72 overflow-y-auto">
          {loading && <div className="text-sm text-ink-500 px-1 py-2">Searching…</div>}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div className="text-sm text-ink-500 px-1 py-2">No matches for &quot;{query.trim()}&quot;</div>
          )}
          {results.map((d) => (
            <button
              key={d._id}
              type="button"
              onClick={() => mutation.mutate(d._id)}
              disabled={mutation.isPending}
              className="text-left px-3 py-2 rounded-lg hover:bg-ink-900/5 flex flex-col disabled:opacity-50"
            >
              <span className="font-medium">{d.name}</span>
              <span className="text-xs text-ink-500">{d.brand}</span>
            </button>
          ))}
        </div>

        {mutation.isError && (
          <p className="text-sm text-accent-red">Couldn&apos;t add that device — try again.</p>
        )}

        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="btn-ghost">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
